import { classNames } from '@repo/ui/src/utils/classNames';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Button, { ButtonVariant } from '../Button';
import React from 'react';

/**
 * ContentGrid - A flexible grid component for displaying various content types
 */

export type BackgroundStyle = 'Light' | 'Dark';

const gridColumnClasses: Record<3 | 4 | 5, string> = {
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4',
  5: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-5',
};

interface ContentGridProps {
  itemsPerRow: 3 | 4 | 5;
  header?: string;
  description?: string;
  link?: { link: string; displayName: string };
  backgroundStyle?: BackgroundStyle;
  children: React.ReactNode;
  isProduct?: boolean; // For styling differences (borders vs gaps)
  // Brand-specific styling
  sectionClassName?: string;
  headerClassName?: string;
  descriptionClassName?: string;
}

const ContentGrid = ({
  itemsPerRow,
  header,
  description,
  link,
  backgroundStyle = 'Light',
  children,
  isProduct = false,
  sectionClassName,
  headerClassName,
  descriptionClassName,
}: ContentGridProps) => {
  const hasLink = link?.link && link?.displayName;
  const isDark = backgroundStyle === 'Dark';
  const showHeaderSection = header || description || hasLink;
  const applyGridMargin = showHeaderSection || !isProduct;

  return (
    <section
      className={classNames('py-gutterLG', sectionClassName)}
      data-testid="content-grid-section"
    >
      <div className="max-w-[1400px] mx-auto">
        {showHeaderSection && (
          <div className="mb-15 px-6">
            {header && <h2 className={headerClassName}>{header}</h2>}

            <div className="flex flex-col md:flex-row md:items-end">
              {description && (
                <p
                  className={classNames(
                    'tra-text-web-body-l mb-0 text-left max-w-3xl',
                    descriptionClassName,
                  )}
                >
                  {description}
                </p>
              )}
              {hasLink && (
                <Button
                  link={link.link}
                  as={Anchor}
                  variant={ButtonVariant.knife}
                  dark={isDark}
                  className="md:ml-auto pt-0"
                  size="small"
                >
                  {link.displayName}
                </Button>
              )}
            </div>
            {header && <hr className="border-2 w-10 border-brand-300 my-5" />}
          </div>
        )}

        <ul
          className={classNames(
            'list-none m-0 grid grid-cols-1 sm:grid-cols-2 px-0',
            gridColumnClasses[itemsPerRow],
            isProduct ? 'gap-0 border-l border-t border-neutral-200' : 'gap-6',
            applyGridMargin && 'mx-6',
          )}
          data-testid="content-grid-list"
        >
          {children}
        </ul>
      </div>
    </section>
  );
};

export default ContentGrid;
